/**
 * catalog.ts — Catálogo de blocos prontos do Studio
 *
 * Aqui ficam as CATEGORIAS da biblioteca lateral e os MODELOS PRONTOS de cada
 * categoria. Cada modelo já vem com conteúdo de exemplo em português, para que
 * o usuário monte uma página completa apenas escolhendo e ajustando textos.
 *
 * Regra de ouro: nenhum modelo inventa bloco novo — todos usam blocos do
 * catálogo único (`common.*`, `layout.*`, `page.*`, `proposal.*`).
 *
 * RELEASE: v1.0.0 - 2026-09-10 - Versão inicial com 20 categorias (Studio,
 *          construtor de blocos prontos)
 */

import { registerPatternCategory, registerPatterns } from './patternRegistry';
import type { StudioPatternNode } from './types';
import type { StudioOutput } from '../types';

/** Atalho para montar um nó de padrão. */
function b(type: string, props: Record<string, unknown> = {}, children?: StudioPatternNode[]): StudioPatternNode {
  return children ? { type, props, children } : { type, props };
}

/** Saídas dos modelos de proposta. */
const WEB: StudioOutput[] = ['web'];
const WEB_PDF: StudioOutput[] = ['web', 'pdf'];



// =============================================================================
// CATEGORIAS
// =============================================================================

[
  { key: 'capa', label: 'Capa', icon: 'layout-panel-top', order: 1, description: 'A primeira dobra da página.' },
  { key: 'menu', label: 'Menu do topo', icon: 'panel-top', order: 2, description: 'Barra de navegação e faixas de aviso.' },
  { key: 'titulos', label: 'Títulos', icon: 'heading', order: 3, description: 'Abertura de seção.' },
  { key: 'texto', label: 'Texto', icon: 'type', order: 4, description: 'Parágrafos e listas.' },
  { key: 'texto-imagem', label: 'Texto e imagem', icon: 'columns-2', order: 5, description: 'Conteúdo ao lado de uma foto.' },
  { key: 'recursos', label: 'Recursos', icon: 'list-checks', order: 6, description: 'Benefícios e diferenciais.' },
  { key: 'galeria', label: 'Galeria', icon: 'images', order: 7, description: 'Fotos de produtos e serviços.' },
  { key: 'video', label: 'Vídeo', icon: 'play', order: 8, description: 'Apresentação em vídeo.' },
  { key: 'numeros', label: 'Números', icon: 'trending-up', order: 9, description: 'Resultados em destaque.' },
  { key: 'depoimentos', label: 'Depoimentos', icon: 'quote', order: 10, description: 'Prova social de clientes.' },
  { key: 'logos', label: 'Logos', icon: 'building-2', order: 11, description: 'Clientes e parceiros.' },
  { key: 'precos', label: 'Preços', icon: 'badge-dollar-sign', order: 12, description: 'Planos e valores.' },
  { key: 'etapas', label: 'Etapas', icon: 'list-ordered', order: 13, description: 'Como funciona, passo a passo.' },
  { key: 'faq', label: 'Perguntas', icon: 'help-circle', order: 14, description: 'Dúvidas frequentes.' },
  { key: 'equipe', label: 'Equipe', icon: 'users', order: 15, description: 'Pessoas por trás do serviço.' },
  { key: 'cta', label: 'Chamada para ação', icon: 'megaphone', order: 16, description: 'Convite para o próximo passo.' },
  { key: 'formulario', label: 'Formulário', icon: 'clipboard-list', order: 17, description: 'Captura de contato.' },
  { key: 'contato', label: 'Contato', icon: 'map-pin', order: 18, description: 'Telefone, e-mail e endereço.' },
  { key: 'rodape', label: 'Rodapé', icon: 'panel-bottom', order: 19, description: 'Fechamento da página.' },
  { key: 'separadores', label: 'Separadores', icon: 'minus', order: 20, description: 'Espaços e divisores.' },
  { key: 'proposta', label: 'Proposta', icon: 'file-text', order: 21, description: 'Blocos do documento comercial.' },
].forEach(registerPatternCategory);

// =============================================================================
// PROPOSTA
// =============================================================================

registerPatterns([
  {
    key: 'proposta.capa',
    category: 'proposta',
    label: 'Capa da proposta',
    preview: 'hero-center',
    outputs: WEB_PDF,
    templateTypes: ['proposal', 'online_proposal'],
    order: 1,
    build: () => [b('proposal.hero', {})],
  },
  {
    key: 'proposta.itens-e-total',
    category: 'proposta',
    label: 'Itens e total',
    preview: 'table',
    outputs: WEB_PDF,
    templateTypes: ['proposal', 'online_proposal'],
    order: 2,
    build: () => [b('proposal.items', {}), b('proposal.totals', {})],
  },
  {
    key: 'proposta.condicoes',
    category: 'proposta',
    label: 'Condições comerciais',
    preview: 'text',
    outputs: WEB_PDF,
    templateTypes: ['proposal', 'online_proposal'],
    order: 3,
    build: () => [b('proposal.conditions', {})],
  },
  {
    key: 'proposta.decisao',
    category: 'proposta',
    label: 'Aceite e recusa',
    preview: 'cta',
    outputs: WEB,
    templateTypes: ['online_proposal'],
    order: 4,
    build: () => [b('action.proposal.decision', {})],
  },
  {
    key: 'proposta.rodape',
    category: 'proposta',
    label: 'Rodapé da proposta',
    preview: 'footer',
    outputs: WEB_PDF,
    templateTypes: ['proposal', 'online_proposal'],
    order: 5,
    build: () => [b('proposal.footer', {})],
  },
  {
    key: 'proposta.completa',
    category: 'proposta',
    label: 'Proposta completa',
    description: 'Capa, itens, total, condições e rodapé.',
    preview: 'list',
    outputs: WEB_PDF,
    templateTypes: ['proposal', 'online_proposal'],
    order: 6,
    build: () => [
      b('proposal.hero', {}),
      b('proposal.items', {}),
      b('proposal.totals', {}),
      b('proposal.conditions', {}),
      b('proposal.footer', {}),
    ],
  },
]);
